/**
 * The per-row action pieces of the campaign-contacts table: the actions cell and the NEW tag. Split
 * out of CampaignContactsModal.tsx to keep that file under the 800-line ceiling; the icon control
 * itself is ContactActionButton.tsx (shared with the admin Manage Contacts table), and the refusal
 * handlers still run in the modal.
 */
import CloseIcon from "@mui/icons-material/Close";
import ForwardToInboxOutlinedIcon from "@mui/icons-material/ForwardToInboxOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { COPY } from "@/constants/campaignContacts";
import ContactActionButton from "./ContactActionButton";
import { CONTACTS_TD_CLASS } from "./ContactsDialog";
import {
  isRemovable,
  isResendable,
  REDEEMED_STATUS,
  resendCooldownHoursLeft,
} from "./campaignContacts-helper";
import type { CampaignContactRow, CampaignContactsLock } from "./types";

/**
 * The per-row actions cell (prototype `.cmp-contacts-rm`): resend, preview, remove — each rendered
 * only when its callback exists.
 *
 * The handlers passed in have already absorbed the refusals; what this cell decides is only how a
 * refused control LOOKS and what its tooltip says, following the prototype's `resendTitle` ladder:
 * the campaign lock first, then whether the gift is resendable, then the cooldown.
 */
export function CampaignContactActionsCell({
  contact,
  lock,
  disabled,
  onResend,
  onPreview,
  onRemove,
  removeBlockedReason,
}: {
  contact: CampaignContactRow;
  lock: CampaignContactsLock | null;
  /** MVP switch — see `actionsDisabled` on the modal's props. Wins over every refusal below. */
  disabled: boolean;
  onResend?: (contact: CampaignContactRow) => void;
  onPreview?: (contact: CampaignContactRow) => void;
  onRemove?: (contact: CampaignContactRow) => void;
  /** Caller-supplied reason this row's remove is blocked, or null. */
  removeBlockedReason: string | null;
}) {
  const lockedTitle =
    lock === "paused"
      ? COPY.actionBlocked.paused
      : lock === "closed"
        ? COPY.actionBlocked.closed
        : null;
  const hoursLeft = resendCooldownHoursLeft(contact);
  const resendBlocked = lock !== null || !isResendable(contact) || hoursLeft > 0;

  function resendTitle(): string {
    if (lockedTitle !== null) return lockedTitle;
    if (!isResendable(contact)) return COPY.actionBlocked.notResendable;
    if (hoursLeft > 0) return COPY.actionBlocked.coolingDown(hoursLeft);
    return COPY.actions.resend;
  }

  const removable = isRemovable(contact);
  function removeTitle(): string {
    if (lockedTitle !== null) return lockedTitle;
    if (removeBlockedReason !== null) return removeBlockedReason;
    if (removable) return COPY.actions.remove(contact.name);
    return contact.status === REDEEMED_STATUS
      ? COPY.actionBlocked.removeRedeemed
      : COPY.actionBlocked.removeEnded;
  }

  return (
    <td className={`${CONTACTS_TD_CLASS} w-[118px] whitespace-nowrap text-right`}>
      {onResend !== undefined && (
        <ContactActionButton
          disabled={disabled}
          refused={resendBlocked}
          title={disabled ? COPY.actions.resend : resendTitle()}
          label={COPY.actions.resendLabel(contact.name)}
          onClick={() => onResend(contact)}
        >
          <ForwardToInboxOutlinedIcon sx={{ fontSize: 18 }} />
        </ContactActionButton>
      )}
      {onPreview !== undefined && (
        <ContactActionButton
          disabled={disabled}
          refused={lock !== null}
          title={disabled ? COPY.actions.preview : (lockedTitle ?? COPY.actions.preview)}
          label={COPY.actions.previewLabel(contact.name)}
          onClick={() => onPreview(contact)}
        >
          <VisibilityOutlinedIcon sx={{ fontSize: 18 }} />
        </ContactActionButton>
      )}
      {onRemove !== undefined && (
        /* A contact that can no longer be removed (redeemed, declined, expired) gets the greyed-out
           X — still focusable, so a click still answers with the refusal toast. */
        <ContactActionButton
          disabled={disabled}
          refused={lock !== null || !removable || removeBlockedReason !== null}
          title={disabled ? COPY.actions.remove(contact.name) : removeTitle()}
          label={COPY.actions.remove(contact.name)}
          onClick={() => onRemove(contact)}
        >
          <CloseIcon sx={{ fontSize: 18 }} />
        </ContactActionButton>
      )}
    </td>
  );
}

/** The "NEW" pill beside a contact added in this session. */
export function NewContactTag() {
  return (
    <span className="ml-2 rounded-full bg-clear-dust px-2 py-0.5 text-[11px] font-bold uppercase text-dark-dust">
      {COPY.newTag}
    </span>
  );
}
